// Client API typé : un seul endroit pour fetch + gestion d'erreurs.
// BASE_URL configurable via VITE_API_BASE_URL (défaut prod).
import type { TgUser } from "./telegram";

export const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ?? "https://api.bornbadkids.com"
).replace(/\/+$/, "");

/** Résultat uniforme : succès typé ou échec avec status + code d'erreur. */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string };

async function request<T>(
  method: "GET" | "POST",
  path: string,
  body?: unknown,
  initData?: string,
): Promise<ApiResult<T>> {
  try {
    const headers: Record<string, string> = {};
    if (body != null) headers["Content-Type"] = "application/json";
    // Auth Telegram : en-tête `Authorization: tma <initData>` (vérifié côté serveur).
    if (initData) headers["Authorization"] = `tma ${initData}`;
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
    });

    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      /* réponse non-JSON : on retombe sur le status brut */
    }

    if (!res.ok) {
      const error =
        (json as { error?: string } | null)?.error ?? `http_${res.status}`;
      return { ok: false, status: res.status, error };
    }
    return { ok: true, data: json as T };
  } catch {
    // Panne réseau / CORS / serveur injoignable.
    return { ok: false, status: 0, error: "network_error" };
  }
}

// ---- Contrat de l'API big-dig-api ----

export interface HealthResponse {
  ok: true;
  service: string;
  startedAt: string;
  now: string;
}

export interface StreakInfo {
  days: number; // jours de connexion consécutifs
  reward: number; // pièces offertes (0 si pas la 1re connexion du jour)
}

export interface VerifyResponse {
  ok: true;
  user: TgUser | null;
  authDate: number;
  streak: StreakInfo | null;
}

export interface Stats {
  games: number;
  best: number;
  total: number;
  rank: number | null;
  byTheme: { theme: string | null; total: number; games: number }[];
}

export interface MeResponse {
  ok: true;
  user: TgUser;
  stats: Stats;
  entitlements: string[];
  coins: number;
  referrals: number;
  boosters: Record<string, number>;
  admin?: boolean;
}

export interface BoosterDef {
  id: string; // slow | double | shield
  coins: number;
}

export interface CatalogItem {
  id: string;
  title: string;
  description: string;
  stars: number;
  coins?: number; // prix en pièces (modes) ; absent = non achetable en pièces
  coinsGrant?: number; // pack : pièces créditées à l'achat (consommable Stars)
  category: string; // "modes" | "styles" | "coins" | "extras"
}

export interface ScoreResponse {
  ok: true;
  stats: Stats;
  coins: number;
  earned: number;
  chestAvailable?: boolean; // coffre du jour pas encore ouvert
}

export interface ChestReward {
  type: "coins" | "booster" | "mode" | "nothing";
  amount?: number;
  booster?: string;
  mode?: string;
}

export interface ChestResponse {
  ok: true;
  reward: ChestReward;
  coins: number;
  canReroll: boolean;
  rerollStars: number;
}

/** Ouvre le coffre du jour (gratuit après une partie ; relances via chest_reroll). */
export function openChest(initData: string): Promise<ApiResult<ChestResponse>> {
  return request<ChestResponse>("POST", "/chest/open", undefined, initData);
}

export interface CoinsPurchaseResponse {
  ok: true;
  entitlements: string[];
  coins: number;
}

export interface CatalogResponse {
  ok: true;
  items: CatalogItem[];
  boosters: BoosterDef[];
}

export interface InvoiceResponse {
  ok: true;
  link: string;
}

export interface ClaimResponse {
  ok: true;
  entitlements: string[];
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  username: string | null;
  total: number;
  games: number;
  me?: boolean;
}

export interface LeaderboardResponse {
  ok: true;
  entries: LeaderboardEntry[];
  me: { rank: number; total: number; name: string; inTop: boolean } | null;
}

export function getHealth(): Promise<ApiResult<HealthResponse>> {
  return request<HealthResponse>("GET", "/health");
}

export function verifyInitData(initData: string): Promise<ApiResult<VerifyResponse>> {
  return request<VerifyResponse>("POST", "/auth/verify", { initData });
}

/** Stats du joueur courant (authentifié). */
export function getMe(initData: string): Promise<ApiResult<MeResponse>> {
  return request<MeResponse>("GET", "/me", undefined, initData);
}

export interface SessionResponse {
  ok: true;
  sid: string;
}

/** Ouvre une session de partie (anti-triche) → jeton signé `sid`.
 *  `booster` (optionnel) est consommé maintenant et gravé dans la session. */
export function startGameSession(
  initData: string,
  payload: { theme: string; mode: string; booster?: string },
): Promise<ApiResult<SessionResponse>> {
  return request<SessionResponse>("POST", "/games/start", payload, initData);
}

/** Achète un booster consommable avec des pièces (authentifié). */
export function buyBooster(
  initData: string,
  booster: string,
): Promise<ApiResult<{ ok: true; coins: number; boosters: Record<string, number> }>> {
  return request<{ ok: true; coins: number; boosters: Record<string, number> }>(
    "POST",
    "/boosters/buy",
    { booster },
    initData,
  );
}

/** Envoie la carte de score (PNG) → id de message préparé pour tg.shareMessage. */
export function createShareCard(
  initData: string,
  image: string,
  score: number,
): Promise<ApiResult<{ ok: true; id: string }>> {
  return request<{ ok: true; id: string }>("POST", "/share-card", { image, score }, initData);
}

/** Soumet un score de fin de partie, lié à une session (authentifié). */
export function submitScore(
  initData: string,
  payload: { score: number; sid: string },
): Promise<ApiResult<ScoreResponse>> {
  return request<ScoreResponse>("POST", "/scores", payload, initData);
}

/** Débloque un article avec des pièces gagnées (authentifié). */
export function buyWithCoins(initData: string, item: string): Promise<ApiResult<CoinsPurchaseResponse>> {
  return request<CoinsPurchaseResponse>("POST", "/purchase/coins", { item }, initData);
}

export interface DailyObjective {
  id: string;
  type: string; // games | maxscore | coins
  target: number;
  reward: number;
  progress: number;
  done: boolean;
  claimed: boolean;
}
export interface DailyResponse {
  ok: true;
  objectives: DailyObjective[];
  coins: number;
}
export interface ClaimDailyResponse {
  ok: true;
  coins: number;
  reward: number;
}

/** Objectifs du jour + progression (authentifié). */
export function getDaily(initData: string): Promise<ApiResult<DailyResponse>> {
  return request<DailyResponse>("GET", "/daily", undefined, initData);
}
/** Réclame la récompense d'un objectif accompli (authentifié). */
export function claimDailyObjective(initData: string, obj: string): Promise<ApiResult<ClaimDailyResponse>> {
  return request<ClaimDailyResponse>("POST", "/daily/claim", { obj }, initData);
}

/** Catalogue d'articles achetables (public). */
export function getCatalog(): Promise<ApiResult<CatalogResponse>> {
  return request<CatalogResponse>("GET", "/catalog");
}

export interface FeaturedResponse {
  ok: true;
  day: string;
  mode: string; // clé du mode du jour
  theme: string; // clé du style du jour
  mult: number; // multiplicateur de pièces
  weekend: boolean; // événement week-end actif (pièces ×N sur tout)
  weekendMult: number;
}

/** Combinaison du jour (mode+style mis en avant, pièces multipliées). */
export function getFeatured(): Promise<ApiResult<FeaturedResponse>> {
  return request<FeaturedResponse>("GET", "/featured");
}

/** Crée un lien de facture Stars pour un article (authentifié). */
export function createInvoice(initData: string, item: string): Promise<ApiResult<InvoiceResponse>> {
  return request<InvoiceResponse>("POST", "/payments/invoice", { item }, initData);
}

/** Débloque un article gratuit (0⭐) sans paiement Telegram (authentifié). */
export function claimItem(initData: string, item: string): Promise<ApiResult<ClaimResponse>> {
  return request<ClaimResponse>("POST", "/payments/claim", { item }, initData);
}

/** Réinitialise MES droits + paiements (réservé aux admins). */
export function resetMe(initData: string): Promise<ApiResult<ClaimResponse>> {
  return request<ClaimResponse>("POST", "/me/reset", undefined, initData);
}

/** Simule l'achat d'un article sans vraies Stars (réservé aux admins, dev). */
export function simulatePurchase(initData: string, item: string): Promise<ApiResult<ClaimResponse>> {
  return request<ClaimResponse>("POST", "/payments/simulate", { item }, initData);
}

/** Top joueurs. `initData` (optionnel) → repère « moi » + mon rang hors top. */
export function getLeaderboard(
  opts: { theme?: string; mode?: string; limit?: number; period?: string } = {},
  initData?: string,
): Promise<ApiResult<LeaderboardResponse>> {
  const q = new URLSearchParams();
  if (opts.theme) q.set("theme", opts.theme);
  if (opts.mode) q.set("mode", opts.mode);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.period) q.set("period", opts.period);
  const qs = q.toString();
  return request<LeaderboardResponse>("GET", `/leaderboard${qs ? `?${qs}` : ""}`, undefined, initData);
}
